import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface SegmentedProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; icon?: LucideIcon }[];
  compact?: boolean;
  label: string;
  className?: string;
}

/**
 * Segmented pill group — a single-select toggle rendered as pills inside a muted
 * track. Shared by the Branding preview tabs / alignment control and the Letters
 * source filter. Pass `compact` for the smaller (preview-tab) size.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  compact,
  label,
  className,
}: SegmentedProps<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("bg-muted flex w-fit gap-0.5 rounded-xl p-[3px]", className)}
    >
      {options.map((o) => {
        const Icon = o.icon;
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-[9px] font-medium transition-colors",
              compact ? "h-7 px-2.5 text-xs" : "h-9 px-3.5 text-[13px]",
              active
                ? "bg-card text-foreground font-semibold shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {Icon && <Icon className="size-3.5" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
